import { Component, ViewChild } from '@angular/core';
import { MessageService, TreeNode } from 'primeng/api';
import { UIChart } from 'primeng/chart';
import { Table } from 'primeng/table';
import { TreeTable } from 'primeng/treetable';
import { ChartService } from '../../services/chart.service';
import { ImportsModule } from '../../imports';
import { Column } from '../Column';
import { DetailTableData } from '../../shared/types';
import { DetailTableComponent } from "../../shared/components/detail-table/detail-table.component";
import { FileService } from '../../services/file.service';

interface ChartData {
  datasets: {
    data: number[],
    backgroundColor: string[]
  }[],
  labels: string[]
}

@Component({
  selector: 'app-libraries',
  standalone: true,
  imports: [
    ImportsModule,
    DetailTableComponent
],
  templateUrl: './libraries.component.html',
  styleUrl: './libraries.component.css'
})
export class LibrariesComponent {
  @ViewChild('dt') dataTable!: TreeTable;
  @ViewChild('ddt') detailTable!: Table;
  @ViewChild('chart') chart!: UIChart;

  scopeOptions: any[] = [
    { label: 'In Scope', value: 'In Scope' },
    { label: 'Out of Scope', value: 'Out of Scope' }
  ];
  scopeValue: any[] = [];
  previousScopeValue: any[] = [];
  phaseOptions: any[] = [
    { label: 'Wave 1', value: 'Wave 1' },
    { label: 'Wave 2', value: 'Wave 2' },
    { label: 'Wave 3', value: 'Wave 3' },
    { label: 'Whatif', value: 'Whatif' }
  ];
  phaseValue: any[] | null = null;
  columns: Column[] = [];
  detailColumns: Column[] = [];
  tableResults: {
    library_type: string,
    library_name: string,
    library_description: string,
    library_count: number,
    object_count: number
  }[] = [];
  treeTableResults: TreeNode[] = [];
  detailTableResults: {
    [key: string]: string,
    scopedesc: string,
    id: string,
    library: string,
    name: string,
    type: string,
    attribute: string,
    text: string,
    srcFile: string,
    srcMember: string,
    lastUser: string
  }[] = [];
  filteredDetailTableResults: any[] = [];
  
  showDetail = false;
  currentLibraryName: string = "";
  showLoader = false;
  globalFilterFields: string[] = [];
  selectedValues: { [key: string]: any[] } = {};
  sortedColumns: any[] = [];
  chartOptions: any;
  detailTableData: DetailTableData = {} as DetailTableData;
  isBusy: boolean = false;

  colors: { [key: string]: string } = {
    'Excluded': '#4AC9FF',
    'Reference Only': '#AEAEAE',
    'Targeted': '#186182'
  };

  selectedMatchModes: { [key: string]: string } = {};
  constructor(
    private chartService: ChartService,
    private messageService: MessageService,
    private fileService: FileService
  ) { }

  results: ChartData = {
    datasets: [{
      data: [],
      backgroundColor: []
    }],
    labels: []
  }
  ngOnInit() {
    this.scopeValue = this.scopeOptions.map((item: any) => item.value);
    this.columns = [
      { label: 'Library Type', field: 'library_type', width: 100 },
      { label: 'Library Name', field: 'library_name', width: 100 },
      { label: "Library Description", field: "library_description", width: 100 },
      { label: "Distinct Library Count", field: "library_count", width: 100, align: "right" },
      { label: "Object Count", field: "object_count", width: 100, align: "right" },
    ];

    this.detailColumns = [
      { label: 'Scope Phase', field: 'scopedesc', width: 100, filterType: 'select',  },  
      { label: 'Library', field: 'library_name', width: 175, filterType: 'text', matchMode: "contains"  },
      { label: 'Name', field: 'object_name', width: 175, filterType: 'text', matchMode: "contains"  },
      { label: 'Type', field: 'object_type', width: 100, filterType: 'select'},
      { label: 'Attribute', field: 'object_attribute', width: 150, filterType: 'select' },
      { label: 'Text', field: 'object_text', width: 200, filterType: 'text', matchMode: "contains"  },
      { label: "Src Match", field: "object_srcMatch", width: 150, filterType: 'text', matchMode: "contains"  },
      { label: "Target", field: "object_target", width: 125, filterType: 'select' },
      { label: "Resolved Scope", field: "resolved_scope", width: 200, filterType: 'select' },
      { label: "Reason", field: "object_reason", width: 175, filterType: 'text', matchMode: "contains"  },
      { label: "Notes", field: "object_notes", width: 175, matchMode: "contains", filterType: 'text'  },
      { label: "Created", field: "object_created", width: 175, filterType: 'date'},
      { label: "Changed", field: "object_changed", width: 175, filterType: 'date' },
      { label: "Last Used", field: "object_lastUsed", width: 175, filterType: 'date' },
      { label: "Restored", field: "object_restored", width: 175, filterType: 'date' }
    ]

    this.globalFilterFields = this.detailColumns.map(col => col.field);

    this.loadSummaryTableData(this.scopeValue.join(','), this.phaseValue);

    this.chartOptions = {
      layout: {
        padding: {
          x: 50, 
          y: 20
        }
      },
      plugins: {
        title: {
          display: true,
          align: 'center',
          color: '#186182',
          font: {
            size: 16
          },
          text: "Objects by Library Type"
        },
        datalabels: {
          display: true,
          formatter: function(value: any, context: any){
            const dataset = context.chart.data.datasets[0].data;
            const total = dataset.reduce((sum: number, val: number) => sum + val, 0);
            const percentage = (value / total * 100).toFixed(0) + '%';
            return percentage;
          },
          color: 'white',
          font: {
            weight: 'bold'
          },
          anchor: 'end',
          align: 'start',
          clamp: false
        }
      }
    }
  }

  loadSummaryTableData(scope?: string | null, phase?: string[] | null) {
    const chartDataSummary = this.chartService.getObjectsByLibrarySummary(scope, phase).subscribe({
      next: (data: any) => {
        if(data.rowsAffected > 0){ 
          this.resetChart();
          data.rows.map((row: { 
                        text: string, 
                        count: number,
                        type: number,
                        library_count: number}) => {
            this.results.labels.push(row.text);
            this.results.datasets[0].data.push(row.count);
            this.results.datasets[0].backgroundColor.push(this.colors[row.text]);
          });
          this.chart.reinit();
          data.rows.map((row: { 
                        text: string, 
                        count: number,
                        type: number,
                        library_count: number}) => {
            const newTreeNode = {
              label: row.text,
              children: [],
              data: {
                library_type: row.text,
                library_name: "",
                library_description: "",
                library_count: row.library_count,
                object_count: row.count
              }
            } as TreeNode;
            this.chartService.getObjectsByLibraryDetail(row.text, scope, phase).subscribe({
              next: (detailData: any) => {
                detailData.rows.map((detailRow: any) => {
                  if(newTreeNode){
                    newTreeNode.children?.push({
                      label: detailRow.text,
                      data: {
                        library_type: row.text,
                        library_name: detailRow.library_name,
                        library_description: detailRow.library_text,
                        library_count: detailRow.library_count,
                        object_count: detailRow.count
                      }
                    });
                  }
                });
              }
            });
            this.treeTableResults = [...this.treeTableResults, newTreeNode];
          });
        }
      },
      error(error: any) {
        console.log(error);
      }
    });
  }

  logRowData(rowData: any) {
    console.log(rowData);
  }

  showDetailedTable(col: any, rowData: any) {
   if(col == "library_name"){
    this.showLoader = true;
    this.detailTableResults = [];
    this.currentLibraryName = rowData.library_name;
    this.chartService.getObjectsByLibraryName(rowData.library_name).subscribe({
      next: (data: any) => {
        if (data.rowsAffected > 0) {
;
          this.detailTableResults = data.rows;
          this.filteredDetailTableResults = this.detailTableResults;

          this.detailTableData = {
            items: data.rows,
            columns: this.detailColumns,
            globalFilterFields: this.globalFilterFields,
            detailTableResults: this.detailTableResults
          }

        }
        this.showDetail = true;
        this.showLoader = false;
      }
    });
   }
  }

  backToSummary() {
    this.showDetail = false;
    this.filteredDetailTableResults = [];
    this.detailTableResults = [];
    this.globalFilterFields = [];
  }

  getColumnOptions(field: string): any[] {
    const uniqueValues = Array.from(new Set(this.detailTableResults.map((item) => item[field])));
    if(uniqueValues.length == 1 && uniqueValues[0] == null) return []
    return uniqueValues.map(value => ({ label: value, value }));
  }

  // filterBySelectedValues(field: string) {
  //   const selectedValues = this.selectedValues[field];
  //   if (selectedValues && selectedValues.length) {
  //     this.filteredDetailTableResults = this.detailTableResults.filter(item => selectedValues.includes(item[field]));
  //   } else {
  //     this.chartService.getObjectsByLibraryName(this.currentLibraryName).subscribe({
  //       next: (data: any) => {
  //         if (data.rowsAffected > 0) {
  //           this.detailTableResults = data.rows;
  //           this.filteredDetailTableResults = this.detailTableResults;
  //         }
  //       },
  //       error(error: any) {
  //         console.log(error);
  //       }
  //     });
  //   }
  // }

  onMatchModeChange(event: any, field: string) {
    this.selectedMatchModes[field] = event.value;
    this.filterBySelectedValues();
  }

  filterBySelectedValues() {
    this.filteredDetailTableResults = this.detailTableResults.filter(item => {
      return Object.keys(this.selectedValues).every(field => {
        const selectedValues: any = this.selectedValues[field];
        if (selectedValues && selectedValues.length) {
          if (Array.isArray(selectedValues)) {
            // Handle select filters
            return selectedValues.includes(item[field] == null ? 'None' : item[field]);
          } else {
            const matchMode = this.selectedMatchModes[field] || 'contains';
            // Handle text filters with match modes
            const value = item[field] ? item[field].toString().toLowerCase() : 'none';
            const filterValue = selectedValues.toString().toLowerCase();
            switch (matchMode) {
              case 'contains':
                return value.includes(filterValue);
              case 'equals':
                return value === filterValue;
              case 'startsWith':
                return value.startsWith(filterValue);
              case 'endsWith':
                return value.endsWith(filterValue);
              default:
                return true;
            }
          }
        }
        return true;
      });
    });
  }

  onScopePhaseChange(event: any, type: string) {
    if (type === 'scope') {
      if(this.scopeValue.length == 0) {
        this.scopeValue[0] = event.option.value;
        return;
      }
    } 
    // else {
    //   this.phaseValue = event.value;
    // }
    this.resetChart();
    this.loadSummaryTableData(this.scopeValue.join(','), this.phaseValue);
    return true
  }

  resetChart() {
    this.results = {
      datasets: [{
        data: [],
        backgroundColor: []
      }],
      labels: []
    }
    this.treeTableResults = [];
  }

  isNumber(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }


  handleSort(event: any) {
    const field = event.field;
    const order = event.order;
    this.filteredDetailTableResults.sort((a, b) => {
      const valueA = a[field] == null ? '' : a[field];
      const valueB = b[field] == null ? '' : b[field];
      let result = 0;
      if (valueA < valueB) {
        result = -1;
      } else if (valueA > valueB) {
        result = 1;
      }
      return order * result;
    });
  }

  async exportExcel() {
    this.isBusy = true;
    this.messageService.add({ severity: 'info', summary: 'Processing', detail: 'The data from this table is being processed into a file', sticky: true, key:'bc' });

    const firstData = this.extractData(this.treeTableResults);
    const buffer = await this.fileService.createExcel(firstData, 'Libraries Detail');
    this.fileService.saveAsExcelFile(buffer, 'Libraries Detail');
    this.isBusy = false;
  }

  extractData(data: TreeNode[]) {
    let allData : any[] = [];

    data.forEach((item: TreeNode) => {
      allData.push(item.data);
      if(item.children){
        item.children.forEach((child: TreeNode) => {
          allData.push(child.data);
          if(child.children){
            child.children.forEach((grandchild: TreeNode) => {
              allData.push(grandchild.data);
            });
          }
        });
      }
    });
    return allData;
  }
}
